import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mic2, ArrowLeft, ListMusic } from "lucide-react";
import { api } from "@/lib/api";

export default function StagePage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [eventName, setEventName] = useState("Karaoke Night");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/dj/login");
    }
  }, [navigate]);

  useEffect(() => {
    load();
    api
      .get("/event")
      .then(({ data }) => setEventName(data.event_name))
      .catch(() => {});
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const playing = bookings.find((b) => b.status === "playing");
  const queue = bookings.filter((b) => b.status === "pending").slice(0, 6);

  return (
    <div className="neon-grid min-h-screen w-full flex flex-col">
      <header className="flex items-center justify-between px-6 lg:px-12 py-6">
        <div className="flex items-center gap-3" data-testid="stage-event-name">
          <Mic2 className="h-7 w-7 text-pink-500" />
          <span className="font-display font-extrabold text-xl lg:text-2xl text-white">
            {eventName}
          </span>
        </div>
        <button
          onClick={() => navigate("/dj/dashboard")}
          data-testid="stage-back-button"
          className="flex items-center gap-2 border border-slate-700 hover:border-pink-500 text-white rounded-xl px-4 py-2 text-sm transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Console
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 lg:px-12 pb-16">
        {/* Ora canta */}
        <div className="w-full max-w-5xl text-center">
          <p className="font-mono text-sm lg:text-base uppercase tracking-[0.4em] text-cyan-400 mb-6 flex items-center justify-center gap-3">
            <span className="relative inline-flex h-3 w-3">
              <span className="live-dot absolute inline-flex h-3 w-3" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400" />
            </span>
            Ora canta
          </p>
          <AnimatePresence mode="wait">
            {playing ? (
              <motion.div
                key={playing.id}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{ duration: 0.4 }}
                data-testid="stage-now-singing"
              >
                <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black text-white text-glow leading-none">
                  {playing.nome} {playing.cognome}
                </h1>
                <p className="mt-8 text-2xl lg:text-4xl text-pink-400 font-semibold">
                  {playing.titolo}
                </p>
                <p className="mt-2 text-lg lg:text-2xl text-slate-400">
                  {playing.artista} · {playing.versione}
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-testid="stage-idle"
              >
                <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black text-slate-600 leading-none">
                  In attesa del prossimo artista…
                </h1>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Prossimi */}
        <div className="w-full max-w-4xl mt-16 lg:mt-24">
          <p className="font-mono text-xs lg:text-sm uppercase tracking-[0.3em] text-slate-500 mb-5 flex items-center gap-2">
            <ListMusic className="h-4 w-4" /> Prossimi in coda
          </p>
          {queue.length === 0 ? (
            <p className="text-slate-600 text-lg" data-testid="stage-queue-empty">
              Nessuno in coda — prenota la tua canzone!
            </p>
          ) : (
            <div className="space-y-3" data-testid="stage-queue">
              <AnimatePresence initial={false}>
                {queue.map((b, i) => (
                  <motion.div
                    key={b.id}
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: i * 0.05 }}
                    data-testid={i === 0 ? "stage-next-up" : undefined}
                    className={`neon-card rounded-xl px-5 lg:px-6 py-4 flex items-center gap-4 ${
                      i === 0 ? "next-glow" : ""
                    }`}
                  >
                    <span
                      className={`font-display font-black text-2xl lg:text-3xl w-10 shrink-0 ${
                        i === 0 ? "text-cyan-400" : "text-pink-500"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-semibold text-lg lg:text-xl truncate">
                          {b.nome} {b.cognome}
                        </p>
                        {i === 0 && (
                          <span className="shrink-0 text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-400/40">
                            Prossimo
                          </span>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm lg:text-base truncate">
                        {b.titolo} — {b.artista}
                      </p>
                    </div>
                    <span className="hidden sm:inline-block text-xs font-mono px-2.5 py-1 rounded-md bg-slate-800/70 border border-slate-700 text-slate-300 shrink-0">
                      {b.versione}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
